import * as Sentry from '@sentry/vue';
import { ref, computed } from 'vue';
import type { Ref, ComputedRef } from 'vue';
import type {
  EventResponse,
  OnboardingStatusResponse,
  PaymentMethodResponse,
} from '@@/types/api';
import type { ViewerRole, TransitionDescriptor } from '~/utils/event-policy';
import { availableTransitions, isFieldEditable } from '~/utils/event-policy';
import { ApiError } from '~/composables/api/api-error';
import { useEventsApi } from '~/composables/api/use-events-api';
import { useStripeApi } from '~/composables/api/use-stripe-api';

export interface TransitionError {
  message: string;
  fields?: Record<string, string[]>;
}

interface UseOrganizerActionsOptions {
  event: Ref<EventResponse | null> | ComputedRef<EventResponse | null>;
  viewerRole: Ref<ViewerRole> | ComputedRef<ViewerRole>;
  refreshEvent: () => Promise<void>;
}

export function useOrganizerActions(options: UseOrganizerActionsOptions) {
  const { event, refreshEvent } = options;
  const { transitionEvent } = useEventsApi();
  const { getOnboardingStatus, listPaymentMethods } = useStripeApi();
  const { trackEvent } = useMatomo();

  // Stripe context
  const stripeStatus = ref<OnboardingStatusResponse | null>(null);
  const paymentMethods = ref<PaymentMethodResponse[]>([]);
  const isLoading = ref(false);

  // Transition state
  const isTransitioning = ref(false);
  const transitionError = ref<TransitionError | null>(null);

  // Dialog state
  const showSubmitReviewDialog = ref(false);
  const showConfirmDialog = ref(false);
  const showSubmitSuccess = ref(false);
  const pendingTransition = ref<TransitionDescriptor | null>(null);

  // Computed
  const primaryTransition = computed(
    () => availableTransitions(event.value?.lifecycle_state)[0] ?? null,
  );

  const canEdit = computed(() => {
    if (!event.value) return false;
    return isFieldEditable('title', event.value);
  });

  const isStripeOnboarded = computed(
    () => stripeStatus.value?.status === 'complete',
  );

  const needsStripe = computed(
    () =>
      primaryTransition.value?.action === 'start_fundraising' &&
      !isStripeOnboarded.value,
  );

  // Actions
  async function initializeStripeContext() {
    isLoading.value = true;
    try {
      const [statusResult, pmResult] = await Promise.allSettled([
        getOnboardingStatus(),
        listPaymentMethods(),
      ]);

      if (statusResult.status === 'fulfilled') {
        stripeStatus.value = statusResult.value;
      }
      if (pmResult.status === 'fulfilled') {
        paymentMethods.value = pmResult.value.data;
      }
    } finally {
      isLoading.value = false;
    }
  }

  const secondaryTransitions = computed(() =>
    availableTransitions(event.value?.lifecycle_state).slice(1),
  );

  function openPrimaryAction() {
    if (!primaryTransition.value) return;
    openAction(primaryTransition.value);
  }

  function openAction(transition: TransitionDescriptor) {
    transitionError.value = null;

    if (transition.action === 'submit_for_review') {
      showSubmitReviewDialog.value = true;
      return;
    }
    if (transition.requiresConfirmation) {
      pendingTransition.value = transition;
      showConfirmDialog.value = true;
    } else {
      executeTransition(transition);
    }
  }

  async function submitForReview(paymentMethodId: string) {
    showSubmitReviewDialog.value = false;
    const transition = availableTransitions(event.value?.lifecycle_state).find(
      (t) => t.action === 'submit_for_review',
    );
    if (transition) {
      await executeTransition(transition, paymentMethodId);
    }
  }

  async function confirmTransition() {
    if (!pendingTransition.value) return;
    showConfirmDialog.value = false;
    await executeTransition(pendingTransition.value);
    pendingTransition.value = null;
  }

  async function handleCardAdded() {
    try {
      const result = await listPaymentMethods();
      paymentMethods.value = result.data;
    } catch (e) {
      Sentry.captureException(e);
    }
  }

  function clearError() {
    transitionError.value = null;
  }

  async function executeTransition(
    transition: TransitionDescriptor,
    paymentMethodId?: string,
  ) {
    if (!event.value) return;
    isTransitioning.value = true;
    transitionError.value = null;
    try {
      await transitionEvent(event.value.id, transition.action, paymentMethodId);
      await refreshEvent();
      trackEvent('Event', transition.action, event.value?.id);
      if (transition.action === 'submit_for_review') {
        showSubmitSuccess.value = true;
      }
    } catch (e: unknown) {
      Sentry.captureException(e, {
        tags: {
          operation: 'event_transition',
          transition_action: transition.action,
          event_id: event.value?.id,
        },
      });
      if (e instanceof ApiError && e.isNetworkError) {
        transitionError.value = {
          message: 'Network error — check your connection and try again.',
        };
      } else if (
        e &&
        typeof e === 'object' &&
        'name' in e &&
        e.name === 'ApiError'
      ) {
        const apiErr = e as unknown as {
          message: string;
          validation: Record<string, string[]> | null;
        };
        transitionError.value = {
          message: apiErr.message,
          fields: apiErr.validation ?? undefined,
        };
      } else {
        transitionError.value = {
          message: 'Failed to update event. Please try again.',
        };
      }
    } finally {
      isTransitioning.value = false;
    }
  }

  return {
    // Computed
    primaryTransition,
    secondaryTransitions,
    canEdit,
    needsStripe,
    isStripeOnboarded,
    // Refs
    stripeStatus,
    paymentMethods,
    isLoading,
    isTransitioning,
    transitionError,
    // Dialog state
    showSubmitReviewDialog,
    showConfirmDialog,
    showSubmitSuccess,
    pendingTransition,
    // Actions
    initializeStripeContext,
    openPrimaryAction,
    openAction,
    submitForReview,
    confirmTransition,
    handleCardAdded,
    clearError,
  };
}
